import React, { useMemo } from 'react';
import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import { capitalizeSentenceFirstLetters } from '@repo/utils';
import { ApiResponse, BulkReferralResponse, BulkReferralFailure, BulkReferralResult } from '@src/api.types';

type NoResponse = null;
type NotSubmitted = undefined;

export type CboReferralSubmissionResultsProps = {
  referResp: ApiResponse<any> | NoResponse | NotSubmitted;
  setReferResp: (resp: ApiResponse<any> | NoResponse | NotSubmitted) => void;
  fromBulkRefer: BulkReferralResult | NotSubmitted;
  setBulkReferResp: (resp: BulkReferralResult | NotSubmitted) => void;
};

const ERROR_SECTION_STYLE: React.CSSProperties = {
  display: 'block',
  // border: '1px solid #d32f2f',
  borderRadius: '4px',
  padding: '16px',
  marginBottom: '16px',
  // backgroundColor: '#ffebee',
};

const SUCCESS_SECTION_STYLE: React.CSSProperties = {
  padding: '16px',
  marginBottom: '16px',
  backgroundColor: '#e8f5e9',
  borderRadius: '4px',
};

const ERROR_TEXT_STYLE: React.CSSProperties = {
  color: '#d32f2f',
  fontWeight: 500,
};

export const CboReferralSubmissionResults: React.FC<CboReferralSubmissionResultsProps> = ({
  referResp,
  setReferResp,
  fromBulkRefer,
  setBulkReferResp,
}) => {
  const bulkData = fromBulkRefer;

  // Memoize bulk referral results to avoid repeated filtering
  const bulkReferralGroups = useMemo(() => {
    const failures = bulkData?.failures ?? [];
    const failed: BulkReferralFailure[] = failures.filter((f) => f.status === 'failure');
    const successInboundFailed: BulkReferralFailure[] = failures.filter((f) => f.status === 'success-inbound failed');

    return {
      failed,
      successInboundFailed,
      hasFailures: (bulkData?.failed ?? 0) > 0,
    };
  }, [bulkData]);

  const isBulkSuccess = useMemo(() => {
    return !!bulkData && bulkData.failed === 0;
  }, [bulkData]);

  const handleCloseBulkModal = () => {return setBulkReferResp(undefined)};
  const handleCloseReferModal = () => {return setReferResp(undefined)};

  // Single referral modal
  if (fromBulkRefer === undefined) {
    return (
      <ModalDialog
        header={referResp?.status ? capitalizeSentenceFirstLetters(referResp.status) : 'Error'}
        isOpen={referResp !== undefined}
        onClose={handleCloseReferModal}
      >
        <div>
          {referResp?.status === 'success' ? (
            'Referral made successfully'
          ) : (
            <span style={ERROR_TEXT_STYLE}>
              {referResp?.message ??
                'Sorry, something went wrong while making your referral. Please try again in a moment.'}
            </span>
          )}
        </div>
      </ModalDialog>
    );
  }

  // Bulk referral modal
  return (
    <ModalDialog
      header={isBulkSuccess ? 'Success' : 'Exception'}
      isOpen={fromBulkRefer !== undefined}
      onClose={handleCloseBulkModal}
    >
      <div>
        {isBulkSuccess && !bulkReferralGroups.successInboundFailed.length ? (
          'Referrals made successfully'
        ) : (
          <React.Fragment>
            {bulkReferralGroups.failed.length > 0 && (
              <div style={ERROR_SECTION_STYLE}>
                <p style={{ margin: '0 0 12px 0' }}>
                  The following referral(s) were not submitted successfully. Please try again or
                  select a different CBO.
                </p>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {bulkReferralGroups.failed.map((program, idx) => {
                    const programDetails = program.auntBerthaReferralsLiteReq?.programDetails;
                    const programName = programDetails?.programName ?? 'Unknown Program';
                    const orgName = programDetails?.programOrgName ?? '';
                    const errorMsg = program.errorMessage ?? 'Unknown error';

                    return (
                      <li key={`failed-${idx}`} style={{ marginBottom: '8px' }}>
                        {programName}, {orgName}
                        <span style={ERROR_TEXT_STYLE}> [{errorMsg}]</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {bulkReferralGroups.successInboundFailed.length > 0 && (
              <div style={SUCCESS_SECTION_STYLE}>
                <p style={{ margin: '0 0 12px 0' }}>
                  The following referral(s) were submitted successfully and will be visible in 24
                  hours.
                </p>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {bulkReferralGroups.successInboundFailed.map((program, idx) => {
                    const programDetails = program.auntBerthaReferralsLiteReq?.programDetails;
                    const programName = programDetails?.programName ?? 'Unknown Program';
                    const orgName = programDetails?.programOrgName ?? '';

                    return (
                      <li key={`success-inbound-${idx}`} style={{ marginBottom: '8px' }}>
                        {programName}, {orgName}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </React.Fragment>
        )}
      </div>
    </ModalDialog>
  );
};
